import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { usePlano, PLANOS_CONFIG } from '../hooks/usePlano'
import { supabase } from '../lib/supabase'
import toast from 'react-hot-toast'

const FEATURES = {
  solo: [
    { ok: true,  txt: 'Buscar cifras na web (por nome ou link)' },
    { ok: true,  txt: 'Transpor cifras e acordes' },
    { ok: true,  txt: 'Entrar em grupos' },
    { ok: true,  txt: 'Setlists ilimitados' },
    { ok: true,  txt: 'Participar de shows como espectador' },
    { ok: true,  txt: '1 show ao vivo por mês' },
    { ok: false, txt: 'Importar PDF, Word e foto' },
    { ok: false, txt: 'Importar por áudio MP3 (IA)' },
  ],
  musico: [
    { ok: true,  txt: 'Tudo do Solo' },
    { ok: true,  txt: '4 shows ao vivo por mês' },
    { ok: true,  txt: 'Importar PDF e Word' },
    { ok: true,  txt: 'Importar por foto' },
    { ok: false, txt: 'Shows ilimitados' },
    { ok: false, txt: 'Importar por áudio MP3 (IA)' },
  ],
  maestro: [
    { ok: true,  txt: 'Tudo do Músico' },
    { ok: true,  txt: 'Shows ao vivo ilimitados' },
    { ok: true,  txt: 'Importar por áudio MP3 (IA)' },
  ],
}

const PLANO_ORDER = ['solo', 'musico', 'maestro']
const DESTAQUE = 'musico'

export default function PlanosPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user, carregarPerfil } = useAuth()
  const { plano: planoAtual } = usePlano()
  const [carregando, setCarregando] = useState(null)
  const [billing, setBilling] = useState('monthly') // 'monthly' | 'annual'
  const pollRef = useRef(null)

  const sucesso = searchParams.get('sucesso') === 'true'
  const expirado = searchParams.get('expirado') === 'true'
  const isAnnual = billing === 'annual'

  // Chama sync-subscription assim que chega em ?sucesso=true
  // para forçar atualização mesmo se o webhook falhar
  async function sincronizarComStripe() {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) return false
      const res = await fetch('/api/stripe/sync-subscription', {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}` },
      })
      const data = await res.json()
      if (data.synced && data.plano && data.plano !== 'free') {
        await carregarPerfil(user.id)
        return true
      }
      return false
    } catch (e) {
      console.error('Erro ao sincronizar com Stripe:', e)
      return false
    }
  }

  // Após retorno do Stripe com ?sucesso=true:
  // 1. Tenta sync imediato com Stripe (fallback para webhook falho)
  // 2. Faz polling do perfil até o plano mudar (máx 40s)
  useEffect(() => {
    if (!sucesso || !user) return

    // Tenta sync imediato após 3s (dá tempo pro webhook processar primeiro)
    const syncTimer = setTimeout(async () => {
      const atualizado = await sincronizarComStripe()
      if (atualizado) {
        clearInterval(pollRef.current)
        return
      }
      // Se sync direto não resolveu, inicia polling
      let tentativas = 0
      const MAX = 20 // 20 × 2s = 40s
      pollRef.current = setInterval(async () => {
        tentativas++
        // A cada 5 tentativas, tenta sync direto com Stripe
        if (tentativas % 5 === 0) {
          const ok = await sincronizarComStripe()
          if (ok) { clearInterval(pollRef.current); return }
        } else {
          await carregarPerfil(user.id)
        }
        if (tentativas >= MAX) clearInterval(pollRef.current)
      }, 2000)
    }, 3000)

    return () => {
      clearTimeout(syncTimer)
      clearInterval(pollRef.current)
    }
  }, [sucesso, user]) // eslint-disable-line

  // Para o polling quando o plano já atualizou
  useEffect(() => {
    if (sucesso && planoAtual !== 'free') {
      clearInterval(pollRef.current)
    }
  }, [sucesso, planoAtual])

  async function assinar(planId) {
    setCarregando(planId)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch('/api/stripe/checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ plan: planId, billing }),
      })
      const data = await res.json()
      if (data.error) throw new Error(data.error)
      window.location.href = data.url
    } catch (e) {
      toast.error('Erro ao abrir checkout: ' + e.message)
    } finally {
      setCarregando(null)
    }
  }

  async function gerenciarAssinatura() {
    setCarregando('portal')
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch('/api/stripe/portal', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
      })
      const { url } = await res.json()
      window.location.href = url
    } catch {
      toast.error('Erro ao abrir portal de assinatura')
    } finally {
      setCarregando(null)
    }
  }

  return (
    <div className="app-container" style={{ background: 'var(--brand)' }}>
      {/* Header */}
      <div style={{ padding: '16px 20px 0', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
        <button onClick={() => navigate(-1)}
          style={{ background: 'none', border: 'none', color: 'var(--muted)', fontSize: 22, cursor: 'pointer' }}>‹</button>
        <div style={{ fontFamily: 'Montserrat,sans-serif', fontSize: 18, fontWeight: 700, flex: 1 }}>Planos</div>
        {planoAtual !== 'free' && (
          <button onClick={gerenciarAssinatura} disabled={carregando === 'portal'}
            style={{ fontSize: 11, padding: '6px 12px', borderRadius: 8, border: '1px solid var(--card-border)', background: 'rgba(255,255,255,0.05)', color: 'var(--muted)', cursor: 'pointer' }}>
            {carregando === 'portal' ? '...' : 'Gerenciar'}
          </button>
        )}
      </div>

      <div className="page-content" style={{ padding: '20px 16px 40px' }}>

        {/* Banner trial expirado */}
        {expirado && !sucesso && (
          <div style={{ background: 'rgba(255,80,80,0.1)', border: '1px solid rgba(255,80,80,0.3)', borderRadius: 12, padding: '14px 16px', marginBottom: 20, textAlign: 'center' }}>
            <div style={{ fontSize: 22, marginBottom: 4 }}>⏰</div>
            <div style={{ fontWeight: 700, color: '#ff6b6b', marginBottom: 4 }}>Seu período grátis encerrou</div>
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>Escolha um plano para continuar usando o CifrasBand.</div>
          </div>
        )}

        {/* Banner sucesso */}
        {sucesso && (
          <div style={{ background: 'rgba(0,207,255,0.1)', border: '1px solid rgba(0,207,255,0.3)', borderRadius: 12, padding: '14px 16px', marginBottom: 20, textAlign: 'center' }}>
            <div style={{ fontSize: 24, marginBottom: 4 }}>🎉</div>
            {planoAtual !== 'free' ? (
              <>
                <div style={{ fontWeight: 700, color: '#00CFFF', marginBottom: 2 }}>Assinatura ativada!</div>
                <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 12 }}>Aproveite todos os recursos do seu plano.</div>
                <button onClick={() => navigate('/grupos')}
                  className="btn btn-primary" style={{ fontSize: 13, padding: '8px 20px' }}>
                  Ir para meus grupos →
                </button>
              </>
            ) : (
              <>
                <div style={{ fontWeight: 700, color: '#00CFFF', marginBottom: 2 }}>Pagamento confirmado!</div>
                <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 12 }}>Ativando seu plano… aguarde um instante.</div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                  <div className="spinner" style={{ width: 16, height: 16, alignSelf: 'center' }} />
                  <button onClick={async () => {
                    toast.loading('Verificando assinatura…', { id: 'sync' })
                    const ok = await sincronizarComStripe()
                    toast.dismiss('sync')
                    if (ok) toast.success('Plano ativado com sucesso!')
                    else toast.error('Aguarde mais alguns segundos e tente novamente.')
                  }}
                    style={{ fontSize: 12, padding: '6px 14px', borderRadius: 8, border: '1px solid rgba(0,207,255,0.4)', background: 'transparent', color: '#00CFFF', cursor: 'pointer' }}>
                    Verificar agora
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Headline */}
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <div style={{ fontFamily: 'Montserrat,sans-serif', fontSize: 22, fontWeight: 800, marginBottom: 6 }}>
            Escolha seu plano
          </div>
          <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.5 }}>
            Cancele quando quiser · Sem fidelidade
          </div>
        </div>

        {/* Toggle mensal / anual */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}>
          <div style={{ display: 'flex', background: 'rgba(255,255,255,0.06)', borderRadius: 50, padding: 4, gap: 4 }}>
            {[['monthly','Mensal'], ['annual','Anual']].map(([v, label]) => (
              <button key={v} onClick={() => setBilling(v)}
                style={{ padding: '7px 20px', borderRadius: 50, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: 'Montserrat,sans-serif', transition: 'all 0.2s',
                  background: billing === v ? 'var(--brand2)' : 'transparent',
                  color: billing === v ? '#fff' : 'var(--muted)' }}>
                {label}
                {v === 'annual' && <span style={{ marginLeft: 6, fontSize: 10, background: '#4ade8033', color: '#4ade80', padding: '2px 6px', borderRadius: 20 }}>−10%</span>}
              </button>
            ))}
          </div>
        </div>
        {isAnnual && (
          <div style={{ textAlign: 'center', fontSize: 12, color: '#4ade80', marginBottom: 16 }}>
            💡 Cobrado anualmente · <strong>Economia de 10%</strong> no total do ano
          </div>
        )}

        {/* Cards de planos */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {PLANO_ORDER.map(planId => {
            const cfg = PLANOS_CONFIG[planId]
            const feats = FEATURES[planId]
            const isAtual = planId === planoAtual
            const isDestaque = planId === DESTAQUE
            const isFree = planId === 'free'
            const loading = carregando === planId
            const precoMensal = cfg.preco
            const precoAnual  = +(cfg.preco * 0.9).toFixed(2) // 10% off

            return (
              <div key={planId} style={{
                background: isDestaque ? 'linear-gradient(135deg, rgba(0,207,255,0.06), rgba(255,140,0,0.06))' : 'rgba(255,255,255,0.04)',
                border: `1px solid ${isAtual ? cfg.cor : isDestaque ? 'rgba(0,207,255,0.3)' : 'var(--card-border)'}`,
                borderRadius: 16,
                padding: '18px 16px',
                position: 'relative',
                overflow: 'hidden',
              }}>
                {/* Badge POPULAR */}
                {isDestaque && !isAtual && (
                  <div style={{ position: 'absolute', top: 12, right: 12, background: 'var(--gold)', color: '#fff', fontSize: 10, fontWeight: 800, padding: '3px 8px', borderRadius: 20, fontFamily: 'Montserrat,sans-serif', letterSpacing: 0.5 }}>
                    POPULAR
                  </div>
                )}

                {/* Badge PLANO ATUAL */}
                {isAtual && (
                  <div style={{ position: 'absolute', top: 12, right: 12, background: cfg.cor, color: '#071629', fontSize: 10, fontWeight: 800, padding: '3px 8px', borderRadius: 20, fontFamily: 'Montserrat,sans-serif' }}>
                    ATUAL
                  </div>
                )}

                {/* Nome e preço */}
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, marginBottom: 12 }}>
                  <div style={{ fontFamily: 'Montserrat,sans-serif', fontSize: 18, fontWeight: 800, color: cfg.cor }}>
                    {cfg.nome}
                  </div>
                  <div style={{ marginBottom: 2, flex: 1 }}>
                    {isFree ? (
                      <span style={{ fontSize: 20, fontWeight: 700 }}>Grátis</span>
                    ) : (() => {
                      const preco = isAnnual ? precoAnual : precoMensal
                      const [int, dec] = preco.toFixed(2).split('.')
                      return (
                        <span>
                          {isAnnual && <span style={{ fontSize: 11, color: 'var(--muted)', textDecoration: 'line-through', marginRight: 4 }}>R${precoMensal.toFixed(2).replace('.',',')}</span>}
                          <span style={{ fontSize: 11, color: 'var(--muted)' }}>R$</span>
                          <span style={{ fontSize: 26, fontWeight: 800 }}>{int}</span>
                          <span style={{ fontSize: 14 }}>,{dec}</span>
                          <span style={{ fontSize: 11, color: 'var(--muted)' }}>/mês</span>
                        </span>
                      )
                    })()}
                  </div>
                </div>

                {/* Features */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginBottom: 16 }}>
                  {feats.map((f, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                      <span style={{ color: f.ok ? '#4ade80' : 'rgba(255,255,255,0.2)', fontSize: 14, flexShrink: 0 }}>
                        {f.ok ? '✓' : '✕'}
                      </span>
                      <span style={{ color: f.ok ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.3)' }}>
                        {f.txt}
                      </span>
                    </div>
                  ))}
                </div>

                {/* CTA */}
                {isFree ? (
                  isAtual ? (
                    <div style={{ textAlign: 'center', fontSize: 13, color: 'var(--muted)', padding: '8px 0' }}>
                      Plano atual
                    </div>
                  ) : null
                ) : isAtual ? (
                  <button onClick={gerenciarAssinatura}
                    style={{ width: '100%', padding: '12px', borderRadius: 10, border: `1px solid ${cfg.cor}`, background: 'transparent', color: cfg.cor, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                    Gerenciar assinatura
                  </button>
                ) : (
                  <button onClick={() => assinar(planId)} disabled={!!carregando}
                    style={{
                      width: '100%', padding: '13px', borderRadius: 10, border: 'none', fontSize: 14, fontWeight: 700, cursor: carregando ? 'wait' : 'pointer',
                      background: isDestaque
                        ? 'linear-gradient(90deg, #FF8C00, #e07000)'
                        : planId === 'maestro'
                        ? 'linear-gradient(90deg, #a855f7, #7c3aed)'
                        : `${cfg.cor}22`,
                      color: isDestaque || planId === 'maestro' ? '#fff' : cfg.cor,
                      opacity: carregando && carregando !== planId ? 0.5 : 1,
                    }}>
                    {loading
                      ? <span className="spinner" style={{ width: 18, height: 18 }} />
                      : planoAtual !== 'free'
                        ? `Mudar para ${cfg.nome}`
                        : isAnnual ? `Assinar anual →` : `Assinar mensal →`}
                  </button>
                )}
              </div>
            )
          })}
        </div>

        {/* Rodapé */}
        <div style={{ marginTop: 28, padding: '16px', background: 'rgba(255,255,255,0.03)', border: '1px solid var(--card-border)', borderRadius: 12 }}>
          <div style={{ fontSize: 11, color: 'var(--gold)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>Perguntas frequentes</div>
          {[
            ['Como funciona o período grátis?', '7 dias grátis ao assinar qualquer plano. Cancele antes do fim e não será cobrado nada.'],
            ['Posso cancelar a qualquer hora?', 'Sim. Sem multa ou fidelidade. O acesso continua até o fim do período pago.'],
            ['Como funciona o limite de shows?', 'O contador reseta no primeiro dia de cada mês. Shows não utilizados não acumulam.'],
            ['Aceitam Pix?', 'O pagamento é via cartão de crédito pelo Stripe. Para outras formas entre em contato pelo suporte.'],
          ].map(([p, r], i) => (
            <div key={i} style={{ marginBottom: i < 3 ? 12 : 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 3 }}>{p}</div>
              <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.5 }}>{r}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
